import React, { useEffect, useRef, useState } from "react";
import { Loader2, LogOut, Upload } from "lucide-react";
import { toast } from "sonner";
import { popupService, formatApiError, resolveImageUrl } from "../../services/catalogService";

// Task 18 — the popup is a site option: activate it, write the text,
// upload the image. Captured emails land in the Leads tab (source "Popup").
export default function PopupPanel({ admin, onLogout }) {
  const [config, setConfig] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    popupService.getAdmin().then(setConfig).catch((e) => toast.error("Could not load popup", { description: formatApiError(e) }));
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      setConfig(await popupService.save({ enabled: config.enabled, title: config.title, text: config.text }));
      toast.success(config.enabled ? "Popup activated" : "Popup saved (inactive)");
    } catch (err) {
      toast.error("Could not save", { description: formatApiError(err) });
    } finally {
      setBusy(false);
    }
  };

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      setConfig(await popupService.uploadImage(file));
      toast.success("Popup image updated");
    } catch (err) {
      toast.error("Upload failed", { description: formatApiError(err) });
    } finally {
      setUploading(false);
    }
  };

  if (config === null) {
    return (
      <div className="max-w-3xl mx-auto text-[#817B74] py-20 text-center" data-testid="admin-popup-panel">
        <Loader2 className="animate-spin inline mr-2" size={16} />Loading…
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto" data-testid="admin-popup-panel">
      <div className="flex items-center justify-between mb-10 gap-4 flex-wrap">
        <div>
          <span className="font-sans text-xs uppercase tracking-[0.24em] text-[#D92A47]">Admin</span>
          <h1 className="font-serif text-4xl md:text-5xl text-[#252525] mt-2">Popup</h1>
          <p className="text-[#817B74] text-sm mt-1">
            Activate a popup with image and contact capture. Subscribers land in the Leads tab (source "Popup").
            Signed in as {admin.email}
          </p>
        </div>
        <button onClick={onLogout} data-testid="admin-logout"
          className="inline-flex items-center gap-2 h-10 px-4 border border-black/15 text-[11px] uppercase tracking-[0.14em] hover:border-[#D92A47] hover:text-[#D92A47]">
          <LogOut size={14} /> Sign out
        </button>
      </div>

      <div className="bg-white border border-black/10 p-6 md:p-8">
        <label className="flex items-center gap-3 cursor-pointer select-none">
          <button
            type="button"
            role="switch"
            aria-checked={config.enabled}
            onClick={() => setConfig({ ...config, enabled: !config.enabled })}
            data-testid="popup-enabled-toggle"
            className={`relative h-7 w-12 rounded-full transition-colors ${config.enabled ? "bg-[#D92A47]" : "bg-black/15"}`}
          >
            <span className={`absolute top-1 h-5 w-5 rounded-full bg-white transition-all ${config.enabled ? "left-6" : "left-1"}`} />
          </button>
          <span className="text-[12px] uppercase tracking-[0.14em] text-[#252525]">
            {config.enabled ? "Popup active on the site" : "Popup inactive"}
          </span>
        </label>

        <div className="grid grid-cols-1 gap-4 mt-8">
          <label className="block">
            <span className="block text-[11px] uppercase tracking-[0.14em] text-[#817B74] mb-1">Title</span>
            <input value={config.title} onChange={(e) => setConfig({ ...config, title: e.target.value })}
              maxLength={120} placeholder="e.g. New varieties are here"
              data-testid="popup-title-input"
              className="w-full h-11 bg-[#F6F2EC] border border-black/15 px-3 text-sm focus:outline-none focus:border-[#D92A47]" />
          </label>
          <label className="block">
            <span className="block text-[11px] uppercase tracking-[0.14em] text-[#817B74] mb-1">Text</span>
            <textarea value={config.text} onChange={(e) => setConfig({ ...config, text: e.target.value })}
              rows={3} maxLength={600} placeholder="Short message shown under the title…"
              data-testid="popup-text-input"
              className="w-full bg-[#F6F2EC] border border-black/15 p-3 text-sm focus:outline-none focus:border-[#D92A47] resize-y" />
          </label>
          <div className="flex items-center gap-4">
            <div className="h-20 w-32 bg-[#EDE8E0] border border-black/10 grid place-items-center overflow-hidden text-[10px] text-[#817B74] uppercase tracking-wide">
              {config.image ? (
                <img src={resolveImageUrl(config.image)} alt="" className="h-full w-full object-cover" data-testid="popup-image-preview" />
              ) : "No image"}
            </div>
            <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="popup-image-upload"
              className="inline-flex items-center gap-2 h-10 px-4 border border-black/15 text-[11px] uppercase tracking-[0.12em] hover:border-[#D92A47] hover:text-[#D92A47] disabled:opacity-60">
              {uploading ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
              {uploading ? "Uploading…" : "Upload image"}
            </button>
            <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden"
              onChange={uploadImage} data-testid="popup-image-input" />
          </div>
        </div>

        <button onClick={save} disabled={busy || (config.enabled && !config.title.trim())} data-testid="popup-save"
          className="mt-8 inline-flex items-center gap-2 h-11 px-6 bg-[#D92A47] text-[#F6F2EC] text-[12px] font-semibold uppercase tracking-[0.14em] hover:bg-[#b71e3a] disabled:opacity-50">
          {busy ? <><Loader2 size={14} className="animate-spin" /> Saving…</> : "Save popup"}
        </button>
        {config.enabled && !config.title.trim() && (
          <p className="text-[#D92A47] text-xs mt-2">An active popup needs a title.</p>
        )}
      </div>
    </div>
  );
}
